/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import clsx from 'clsx';
import {
  addLeadTaskAssignment,
  deleteLeadTaskAssignment,
  getLeadTaskComments,
  addLeadTaskComment,
  editLeadTaskComment,
  deleteLeadTaskComment,

  addProjectTaskAssignment,
  deleteProjectTaskAssignment,
  getProjectTaskComments,
  addProjectTaskComment,
  editProjectTaskComment,
  deleteProjectTaskComment,

  addGeneralTaskAssignment,
  deleteGeneralTaskAssignment,
  getGeneralTaskComments,
  addGeneralTaskComment,
  editGeneralTaskComment,
  deleteGeneralTaskComment,
} from 'api/tasks';
import {
  updateTaskStore as updateTaskStoreAction,
  updateEventStore as updateEventStoreAction,
} from 'containers/Events/actions';
import { css } from '@emotion/react';
import {
  getLeadAssignments as getLeadAssignmentsAction,
} from 'containers/Leads/actions';
import { useAction } from 'hooks/useAction';
import DateTimeIcon from 'assets/images/calendar/icon-datetime.svg';
import LeadIcon from 'assets/images/calendar/icon-lead.svg';
import ProjectIcon from 'assets/images/calendar/icon-project.svg';
import ServiceTypeIcon from 'assets/images/calendar/icon-service-type.svg';
import VisibilityIcon from 'assets/images/calendar/icon-visibility.svg';
import { EVENT_COLORS } from 'utils/constants';
import MemberAssignment from './MemberAssignment';
import TaskComments from './TaskComments';

export const TASK_TYPES = {
  lead: 'leads',
  project: 'weddings',
  general: 'loginTasks',
};

const getTaskType = (leadId, weddingId) => {
  if (leadId) return TASK_TYPES.lead;
  if (weddingId) return TASK_TYPES.project;
  return TASK_TYPES.general;
};

const getTaskLink = (taskType, orgId, leadId, weddingId, taskId) => {
  if (taskType === TASK_TYPES.lead) {
    return `/app/pro/${orgId}/leads/lead/${leadId}/item/${taskId}?return_to=calendar`;
  }

  if (taskType === TASK_TYPES.project) {
   return `/app/project/${weddingId}/tools/checklist/all/item/${taskId}`;
  }

  return null;
};

const TaskCard = ({
  eventDetails,
  orgMembers,
  onToggleCompleted,
  project,
  timeZone,
  userId,
  userDetails,
}) => {
  const {
    dueDate,
    details,
    leadId,
    leadFullName,
    leadStatus,
    orgId,
    taskId,
    assigned,
    planningCategories,
    clientVisible,
    completed,
    // location,
    projectName: leadProjectName,
  } = eventDetails.extendedProps;
  const {
    // TODO: This should be handled more similarly to project date events
    // image306,
    id: weddingId,
    name: projectName,
    weddingPartners: rawWeddingPartners,
  } = project || {};
  const { dateFormat } = userDetails || {};

  const [taskAssignments, setTaskAssignments] = useState([]);
  const [taskComments, setTaskComments] = useState([]);
  const [taskCompleted, setTaskCompleted] = useState(completed);

  const updateTaskStore = useAction(updateTaskStoreAction);
  const updateEventStore = useAction(updateEventStoreAction);
  const getLeadAssignments = useAction(getLeadAssignmentsAction);

  const leadAssignments = useSelector((state) => state.leads.leadAssignments);

  const taskType = getTaskType(leadId, weddingId);
  const taskLink = getTaskLink(taskType, orgId, leadId, weddingId, taskId);

  const mapWeddingPartner = ({ profileImage, loginId, ...rest }) => ({
    ...rest,
    id: loginId,
    profile_image: profileImage,
  });

  const weddingPartners = useMemo(() => (rawWeddingPartners || []).map(mapWeddingPartner),[rawWeddingPartners]);

  const assignees = useMemo(() => {
    if (taskType === TASK_TYPES.project) return weddingPartners;
    if (taskType === TASK_TYPES.lead) {
      return orgMembers.filter(member => {
        const { effective_permissions: permissions, id } = member;
        // Org member has access to all leads if lead.all is true.
        if (permissions['lead.all']) return true;
        // Org member has access to leads but only leads assigned to
        // or created by org member.
        if (permissions['lead.full-access']) {
          // If org member is in list of lead assignments
          const assignments = leadAssignments[leadId];
          const isAssigned = assignments?.some(assignment => assignment.loginId === id);
          if (isAssigned) return true;
        }
        return false;
      });
    }
    return orgMembers;
  }, [taskType, weddingPartners, leadAssignments, orgMembers]);

  const membersById = useMemo(
    () =>
      Object.fromEntries(
        (orgMembers || []).map((member) => [member.id, member]),
      ),
    [assignees],
  );

  const transformAssignments = (assignments) => {
    return assignments.map(assignment => {
      // Project task assignments can include clients which aren't apart of
      // org member list.
      const user = membersById[assignment.loginId] ||
        rawWeddingPartners.find(partner => partner.loginId === assignment.loginId) || {};
      return ({
        // #TODO: Should standardize whether we use loginId or id for the assignments.
        // clients don't automatically have id, so they can't be deleted since member.id is needed.
        id: assignment.loginId,
        ...assignment,
        ...user,
        loginId: assignment.loginId,
      });
    });
  };

  const transformAssignmentsForTasks = (assignments) => {
    return assignments.map(assignment => {
      const { loginId } = assignment;
      const login = membersById[loginId] ||
        rawWeddingPartners.find(partner => partner.loginId === loginId) || null;

      if (!login) return { fullname: '(Unknown)' };
      if (!login.fullname) {
        // Find returns a reference so we need to return a new object if we are dealing
        // with a found client, which should be the case if fullname isn't present.
        const fullname = login.name ? login.name : `${login.firstname} ${login.lastname}`;
        return {
          ...assignment,
          login: { ...login, fullname },
          login_id: assignment.loginId,
        };
      }

      return {
        ...assignment,
        login,
        login_id: assignment.loginId,
      };
    });
  };


  const addAssignment = async (member) => {
    let res;

    if (taskType === TASK_TYPES.lead) {
      res = await addLeadTaskAssignment(orgId, leadId, member.id, taskId);
    }

    if (taskType === TASK_TYPES.project) {
      res = await addProjectTaskAssignment(weddingId, member.id, taskId);
    }

    if (taskType === TASK_TYPES.general) {
      res = await addGeneralTaskAssignment(member.id, taskId);
    }

    if (res.ok) {
      const newAssignments = transformAssignmentsForTasks(
        [...taskAssignments, { ...member, loginId: member.id }],
      );
      // Clients don't have loginId as part of the object schema, so we're always going
      // to force it here for now.
      setTaskAssignments(prev => [...prev, { ...member, loginId: member.id }]);
      updateTaskStore({ id: taskId, taskType, data: { assignments: newAssignments } });
      updateEventStore({ id: eventDetails.id, data: { assigned: newAssignments } });
    }
  };

  const removeAssignment = async (member) => {
    let res;

    if (taskType === TASK_TYPES.lead) {
      res = await deleteLeadTaskAssignment(orgId, leadId, member.id, taskId);
    }

    if (taskType === TASK_TYPES.project) {
      res = await deleteProjectTaskAssignment(weddingId, member.id, taskId);
    }

    if (taskType === TASK_TYPES.general) {
      res = await deleteGeneralTaskAssignment(member.id, taskId);
    }

    if (res.ok) {
      const newAssignments = transformAssignmentsForTasks(taskAssignments.filter(m => m.id !== member.id));
      setTaskAssignments(prev => prev.filter(m => m.id !== member.id));
      updateTaskStore({ id: taskId, taskType, data: { assignments: newAssignments } });
      updateEventStore({ id: eventDetails.id, data: { assigned: newAssignments } });
    }
  };

  const addComment = async (comment) => {
    let res;

    if (taskType === TASK_TYPES.lead) {
      res = await addLeadTaskComment(orgId, leadId, taskId, comment);
    }

    if (taskType === TASK_TYPES.project) {
      res = await addProjectTaskComment(weddingId, taskId, comment);
    }

    if (taskType === TASK_TYPES.general) {
      res = await addGeneralTaskComment(taskId, comment);
    }

    if (res.ok) setTaskComments(prev => [...prev, res.data]);
  };

  const editComment = async (commentId, comment) => {
    let res;

    if (taskType === TASK_TYPES.lead) {
      res = await editLeadTaskComment(orgId, leadId, taskId, commentId, comment);
    }

    if (taskType === TASK_TYPES.project) {
      res = await editProjectTaskComment(weddingId, taskId, commentId, comment);
    }

    if (taskType === TASK_TYPES.general) {
      res = await editGeneralTaskComment(taskId, commentId, comment);
    }

    if (res.ok) setTaskComments(prev => prev.map(c => c.id === res.data.id ? res.data : c));
  };

  const deleteComment = async (commentId) => {
    let res;

    if (taskType === TASK_TYPES.lead) {
      res = await deleteLeadTaskComment(orgId, leadId, taskId, commentId);
    }

    if (taskType === TASK_TYPES.project) {
      res = await deleteProjectTaskComment(weddingId, taskId, commentId);
    }

    if (taskType === TASK_TYPES.general) {
      res = await deleteGeneralTaskComment(taskId, commentId);
    }

    if (res.ok) setTaskComments(prev => prev.filter(c => c.id !== commentId));
  };

  const fetchComments = async () => {
    let res;

    if (taskType === TASK_TYPES.lead) {
      res = await getLeadTaskComments(orgId, leadId, taskId);
    }

    if (taskType === TASK_TYPES.project) {
      res = await getProjectTaskComments(weddingId, taskId);
    }

    if (taskType === TASK_TYPES.general) {
      res = await getGeneralTaskComments(taskId);
    }

    if (res.ok) setTaskComments(res.data);
  };

  const taskColor = EVENT_COLORS?.checklist || '#fff';

  useEffect(() => {
    setTaskAssignments(transformAssignments(assigned));
    getLeadAssignments({ orgId, leadId });
    fetchComments();
  }, []);

  useEffect(() => {
    setTaskCompleted(completed);
  }, [completed]);

  return (
    <div className="task-card">
      <div className="task-card__content">
        <div className="task-card__head">
          <div
            css={css`
              background-color: ${taskColor};
            `}
            className={clsx('task-card__checkbox', taskCompleted && 'completed')}
            onClick={() => {
              onToggleCompleted(eventDetails, taskCompleted);
              setTaskCompleted(!taskCompleted);
            }}
          />
          <div className={clsx('task-card__title', taskCompleted && 'completed')}>
            {eventDetails.title}
          </div>
        </div>
        <div className="task-card__info">
          <div className="task-card__icon">
            <img
              src={DateTimeIcon}
              alt="Date-time icon"
            />
          </div>
          <span className="task-card__text">
            Due on {dueDate ? dayjs(dueDate).format(dateFormat) : '-'}
          </span>
        </div>
        {taskType === TASK_TYPES.lead ? (
          <div className="task-card__info">
            <div className="task-card__icon">
              <img
                src={LeadIcon}
                alt="Lead icon"
              />
            </div>
            <span className="task-card__text">
              {leadFullName || '-'} <span className="lead-status">{leadStatus}</span>
            </span>
          </div>
        ) : null}

        {(taskType === TASK_TYPES.lead || taskType === TASK_TYPES.project) && (
          <div className="task-card__info">
            <div className="task-card__icon">
              <img
                src={ProjectIcon}
                alt="Project icon"
              />
            </div>
            <span className="task-card__text">{projectName || leadProjectName || '-'}</span>
          </div>
        )}

        {taskType === TASK_TYPES.project ? (
          <>
            <div className="task-card__info">
              <div className="task-card__icon">
                <img
                  src={ServiceTypeIcon}
                  alt="Service type icon"
                />
              </div>
              <span className="task-card__text">
                {planningCategories?.join(', ') || 'Uncategorized'}
              </span>
            </div>
            <div className="task-card__info">
              {!clientVisible && (
                <>
                  <div className="task-card__icon">
                    <img
                      src={VisibilityIcon}
                      alt="Visibility icon"
                    />
                  </div>
                  <span className="task-card__text">Visible to org members only</span>
                </>
              )}
            </div>
          </>
        ) : null}
        <div className="task-card__description">{details}</div>
        <div className="task-card__assigned">
          <MemberAssignment
            members={assignees}
            assignments={taskAssignments}
            onAdd={addAssignment}
            onRemove={removeAssignment}
          />
        </div>
        <div className="task-card__comments">
          <TaskComments
            comments={taskComments}
            addComment={addComment}
            editComment={editComment}
            deleteComment={deleteComment}
            timeZone={timeZone}
            userId={userId}
          />
        </div>
      </div>
      {taskLink ? (
        <a
          href={taskLink}
          rel="noreferrer"
          className="button button--sm task-card__button"
        >
          View Task
        </a>
      ) : null}
    </div>
  );
};

export default TaskCard;
